import {Permissions, RoleData} from "@/enums/user";

/**
 * The permission every navigable route requires, keyed by its Next.js route pattern with route
 * groups stripped and dynamic segments left as written (`[id]`, `[...slug]`, `[[...id]]`).
 *
 * A trailing `**` gates a whole subtree, root included, and a more specific entry always beats it,
 * so a subtree wildcard plus an override is how a page that needs a different level is expressed.
 * Use one only where three or more pages share a gate: below that it buys less than the indirection
 * costs. The tradeoff is real — a new page under a wildcard inherits its gate silently, where an
 * enumerated subtree would have failed the coverage test until someone chose one (GAIL-785). Keep
 * subtrees whose children disagree enumerated, because there the silent inheritance would be an
 * over-grant: `/outbound/scripts/share` creates, and the rest of `/outbound/scripts` only reads.
 *
 * See docs/authz/README.md.
 */

export type PermissionRouteGate = {
  kind: "permission";
  /** Granted when *any* of these is held at `level`, mirroring nav `additionalDataTypes`. */
  dataTypes: readonly [RoleData, ...RoleData[]];
  level: Permissions;
  /** Extra CRUD levels that also grant the route. Any one of `level` or these is enough. */
  additionalLevels?: readonly [Permissions, ...Permissions[]];
};

export type RouteGate = PermissionRouteGate | {kind: "exempt"; reason: string};

/**
 * Create or update — management UIs that must not appear for a read-only picker role.
 * `assistant.read` stays on `GET /.internal/assistants` (GailGPT picker) and `GET /.internal/skills`.
 */
export const CREATE_OR_UPDATE_LEVELS = [Permissions.Create, Permissions.Update] as const;

export function permissionGateLevels(gate: PermissionRouteGate): readonly Permissions[] {
  return gate.additionalLevels?.length ? [gate.level, ...gate.additionalLevels] : [gate.level];
}

const read = (...dataTypes: readonly [RoleData, ...RoleData[]]): RouteGate => ({
  kind: "permission",
  dataTypes,
  level: Permissions.Read,
});

const write = (dataType: RoleData, level: Permissions): RouteGate => ({
  kind: "permission",
  dataTypes: [dataType],
  level,
});

const createOrUpdate = (...dataTypes: readonly [RoleData, ...RoleData[]]): RouteGate => ({
  kind: "permission",
  dataTypes,
  level: Permissions.Create,
  additionalLevels: [Permissions.Update],
});

const exempt = (reason: string): RouteGate => ({kind: "exempt", reason});

/** Next.js routes every unmatched path here, so resolving to it means nothing gated the page. */
export const NOT_FOUND_PATTERN = "/[...slug]";

export const ROUTE_GATES: Record<string, RouteGate> = {
  // ── Dashboard ─────────────────────────────────────────────────────────────
  "/": read(RoleData.Dashboard),

  // ── Reporting ─────────────────────────────────────────────────────────────
  // One rule for the whole surface. Galactus has no Report controller; the page gate plus
  // Metabase JWT minting (`canMintMetabaseJwt`) are the enforcement. Reporting pages also
  // apply the product axis via `canAccessEodReport` / `canAccessReportingDashboard`.
  "/reporting/**": read(RoleData.Reporting),

  // ── GailGPT ───────────────────────────────────────────────────────────────
  "/gailgpt": read(RoleData.GPT),
  "/gailgpt/[conversationId]": read(RoleData.GPT),
  "/history": read(RoleData.GPT),

  // ── Inbound ───────────────────────────────────────────────────────────────
  "/inbound/scripts": read(RoleData.Inbound),
  "/inbound/scripts/[scriptId]": read(RoleData.Inbound),
  "/inbound/script/[scriptId]/[revision]": read(RoleData.Inbound),
  "/inbound/directory": read(RoleData.Inbound),
  "/inbound/data-extraction": read(RoleData.Inbound),
  "/inbound/transfer-rules": read(RoleData.Inbound),
  "/inbound/actions": read(RoleData.UseCases),
  "/inbound/actions/[actionId]": read(RoleData.UseCases),

  // ── Outbound ──────────────────────────────────────────────────────────────
  "/outbound/campaigns/**": read(RoleData.Campaigns),
  "/outbound/sequences": read(RoleData.Sequences),
  "/outbound/sequences/[id]": read(RoleData.Sequences),
  "/outbound/redialing-rules": read(RoleData.RedialingRules),
  "/outbound/redialing-rules/[id]": read(RoleData.RedialingRules),
  "/outbound/scripts": read(RoleData.Scripts),
  "/outbound/scripts/[scriptId]": read(RoleData.Scripts),
  "/outbound/scripts/text/[scriptId]": read(RoleData.Scripts),
  "/outbound/scripts/email/[scriptId]": read(RoleData.Scripts),
  "/outbound/scripts/blaster/[scriptId]": read(RoleData.Scripts),
  "/outbound/script/[scriptId]/[revision]": read(RoleData.Scripts),
  "/outbound/script/text/[scriptId]/[revision]": read(RoleData.Scripts),
  "/outbound/script/email/[scriptId]/[revision]": read(RoleData.Scripts),
  "/outbound/script/blaster/[scriptId]/[revision]": read(RoleData.Scripts),
  // Both share routes exist to *import* someone else's scripts into this org, so they need the
  // scope that creates scripts rather than the one that reads them.
  "/outbound/scripts/share": write(RoleData.Scripts, Permissions.Create),
  "/outbound/scripts/share/[shareId]": write(RoleData.Scripts, Permissions.Create),
  "/outbound/actions": read(RoleData.UseCases),
  "/outbound/actions/[actionId]": read(RoleData.UseCases),
  "/outbound/directory": read(RoleData.Outbound),
  "/outbound/data-extraction": read(RoleData.Outbound),
  "/evals": exempt(
    "Server-side redirect to /outbound and nothing else. The destination carries the gate."
  ),

  // ── Contacts & calls ──────────────────────────────────────────────────────
  "/communications/contacts": read(RoleData.Contacts),
  "/communications/contacts/[id]": read(RoleData.Contacts),
  "/communications/contact-lists": read(RoleData.ContactLists),
  "/communications/contact-lists/[id]": read(RoleData.ContactLists),
  // `DoNotCallController` is `[Resource(Contact)]`, so the suppression list rides on Contacts.
  "/communications/do-not-call": read(RoleData.Contacts),
  "/calls/**": read(RoleData.Activity),
  "/conversations": read(RoleData.Messaging),
  "/conversations/[id]": read(RoleData.Messaging),

  // ── Assistants & knowledge ────────────────────────────────────────────────
  // Management UI, not the chat picker. `assistant.read` stays on
  // `GET /.internal/assistants` (GailGPT picker) and `GET /.internal/skills`.
  "/assistants/**": createOrUpdate(RoleData.Assistants),
  "/skills": createOrUpdate(RoleData.Assistants),
  // Tabs span three resources. The route admits any of them at create/update because the page
  // drops denied tabs and redirects to the first one the role can open; gating it on Knowledge
  // alone would bounce an agent-profile editor off a page that works for them.
  "/knowledge/[type]": createOrUpdate(RoleData.Knowledge, RoleData.Agent, RoleData.Settings),

  // ── Workflows ─────────────────────────────────────────────────────────────
  "/workflows": read(RoleData.Workflows),
  "/workflows/[id]": read(RoleData.Workflows),
  "/workflows/[id]/builder": write(RoleData.Workflows, Permissions.Update),
  "/workflows/[id]/embed-form": write(RoleData.Workflows, Permissions.Update),
  "/workflows/embed-form": write(RoleData.Workflows, Permissions.Update),
  // Both JotForm surfaces author a workflow — the setup conversation publishes one, and the
  // install page rotates the submission credential — so they gate like embed-form, not like a read.
  "/workflows/[id]/jotform": write(RoleData.Workflows, Permissions.Update),
  "/workflows/jotform": write(RoleData.Workflows, Permissions.Update),
  "/workflows/templates": read(RoleData.Workflows),
  "/workflows/variables": read(RoleData.Workflows),
  "/workflows/variables/secrets": read(RoleData.Workflows),

  // ── Integrations ──────────────────────────────────────────────────────────
  "/marketplace": read(RoleData.Integrations),
  "/integrations/**": read(RoleData.Integrations),
  "/partners": read(RoleData.Integrations),
  "/partners/hooks": read(RoleData.Integrations),

  // ── Settings & support ────────────────────────────────────────────────────
  "/settings/[section]": read(RoleData.Settings),
  "/gail-university": read(RoleData.Support),
  // Org-scoped hosted documents every member of the org may read. Galactus scopes the list and
  // each site to the caller's org (another org's id is a 404), so the page gate admits any role.
  "/sites/**": read(RoleData.Support),
  // Reached from the inbound receptionist setup and org customizations to write the org's smart
  // setup, so it takes the scope that edits inbound configuration.
  "/smart-onboarding": write(RoleData.Inbound, Permissions.Update),

  // ── Ungated by design ─────────────────────────────────────────────────────
  "/permissions": exempt(
    "Renders the caller's own permission matrix from the auth seed. There is no org data to protect and gating it would hide the page that explains why other pages are hidden."
  ),
  "/profile": exempt("The caller's own Auth0 profile. Not org data."),
  "/profile/calendar": exempt("The caller's own calendar connection. Not org data."),
  "/unauthorized": exempt(
    "The destination the route guard redirects to. Gating it would loop the guard."
  ),
  "/start": exempt(
    "Post-signup landing shown before products and roles are provisioned, when the matrix is not yet meaningful."
  ),
  "/upgrade": exempt(
    "Product upsell copy shown to orgs with no active products. Renders no org data."
  ),
  [NOT_FOUND_PATTERN]: exempt(
    "Catch-all that renders the not-found view. Next.js routes any unmatched path here, so it must never redirect."
  ),

  // ── Outside the authenticated customer shell ──────────────────────────────
  // These render under their own route groups, before a session exists or without one at all, so
  // there is no permission matrix to consult.
  "/account-suspended": exempt("Public recovery screen for denied login and suspended sessions; exposes no account data."),
  "/get-started": exempt("Unauthenticated sign-up entry."),
  "/springboard": exempt("Onboarding springboard, reached before org provisioning completes."),
  "/onboarding": exempt("Onboarding flow, reached before org provisioning completes."),
  "/get-gail": exempt("Product request page for users with no product role."),
  "/get-gail/success": exempt("Confirmation for the product request above."),
  "/gpt/[orgId]/[agentId]": exempt(
    "Public GailGPT agent chat. Authenticated by the org/agent pair in the URL, not by a session."
  ),
  "/embed/form/[formId]": exempt(
    "Public embeddable form iframe. The route handler enforces the origin allowlist and form status."
  ),
};

type CompiledGate = {pattern: string; segments: string[]; gate: RouteGate};

const OPTIONAL_CATCH_ALL = "[[...";
const CATCH_ALL = "[...";
const SUBTREE = "**";

const toSegments = (path: string): string[] => path.split("/").filter(Boolean);

const isOpenEnded = (segment: string): boolean =>
  segment === SUBTREE || segment.startsWith(CATCH_ALL) || segment.startsWith(OPTIONAL_CATCH_ALL);

/** Static segments outrank dynamic ones, which outrank the open-ended ones that act as fallbacks. */
const segmentRank = (segment: string): number => {
  if (isOpenEnded(segment)) return 0;
  if (segment.startsWith("[")) return 1;
  return 10;
};

const endsOpen = (segments: string[]): boolean => segments.some(isOpenEnded);

/**
 * Compares left to right rather than by a summed score, so the first segment where two patterns
 * disagree decides it — the same rule Next.js itself applies. Summing would let a late static
 * segment outweigh an early one and hand `/x/y/z` to `/[a]/y/z` over `/x/[b]/[c]`.
 */
function compareSpecificity(a: string[], b: string[]): number {
  for (let index = 0; index < Math.min(a.length, b.length); index += 1) {
    const byRank = segmentRank(b[index]) - segmentRank(a[index]);
    if (byRank !== 0) return byRank;
  }
  // `/calls` and `/calls/**` agree on every segment they share, and an exact gate for a subtree
  // root has to beat the wildcard covering it rather than depend on declaration order.
  return Number(endsOpen(a)) - Number(endsOpen(b));
}

const compile = (table: Record<string, RouteGate>): CompiledGate[] =>
  Object.entries(table)
    .map(([pattern, gate]) => {
      const segments = toSegments(pattern);
      const openIndex = segments.findIndex(isOpenEnded);
      if (openIndex !== -1 && openIndex !== segments.length - 1) {
        // Would silently never match, so fail at import rather than leave a dead gate in the table.
        throw new Error(
          `Route gate "${pattern}" puts an open-ended segment before the end of the pattern. ` +
            `\`**\` and catch-alls only match as the final segment.`
        );
      }
      return {pattern, segments, gate};
    })
    .sort(
      (a, b) =>
        compareSpecificity(a.segments, b.segments) ||
        // Nothing in the table ties this far, but the winner must not depend on key order.
        a.pattern.localeCompare(b.pattern)
    );

function segmentsMatch(patternSegments: string[], pathSegments: string[]): boolean {
  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index];

    if (patternSegment === SUBTREE || patternSegment.startsWith(OPTIONAL_CATCH_ALL)) {
      // Matches the remainder including nothing at all, and only ever appears last.
      return index === patternSegments.length - 1;
    }
    if (patternSegment.startsWith(CATCH_ALL)) {
      return index === patternSegments.length - 1 && pathSegments.length > index;
    }
    if (index >= pathSegments.length) return false;
    if (patternSegment.startsWith("[")) continue;
    if (patternSegment !== pathSegments[index]) return false;
  }

  return pathSegments.length === patternSegments.length;
}

export type RouteGateMatch = {pattern: string; gate: RouteGate};

/**
 * Resolves paths against an arbitrary table. Exported so the precedence rules can be tested on a
 * table small enough to read, rather than only through whatever `ROUTE_GATES` happens to hold.
 */
export function createRouteGateMatcher(
  table: Record<string, RouteGate>
): (pathname: string) => RouteGateMatch | null {
  const compiled = compile(table);

  return (pathname) => {
    const pathSegments = toSegments(pathname.split("?")[0].split("#")[0]);
    const winner = compiled.find((candidate) => segmentsMatch(candidate.segments, pathSegments));

    return winner ? {pattern: winner.pattern, gate: winner.gate} : null;
  };
}

/**
 * The winning gate and the pattern that produced it. The coverage tests need the pattern: with a
 * catch-all in the table every path matches something, so "a gate exists" is not on its own
 * evidence that anyone gated the page.
 */
export const matchRouteGateEntry = createRouteGateMatcher(ROUTE_GATES);

/** The gate for a live pathname, or `null` when no pattern claims it. */
export function matchRouteGate(pathname: string): RouteGate | null {
  return matchRouteGateEntry(pathname)?.gate ?? null;
}
